import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import { filterMarkersForFleetMap } from "./fleetLocationSearch";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const LEAFLET_COLOR_NAMES = [
  "blue",
  "gold",
  "red",
  "green",
  "orange",
  "yellow",
  "violet",
  "grey",
  "black",
];
const COLOR_MARKERS_BASE =
  "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img";
const markerShadowUrl = require("leaflet/dist/images/marker-shadow.png");
/** Fallback when there are no pins. */
const MAP_CENTER = [36.2, -120.4];
const MAP_ZOOM = 6;
const FIT_PADDING = [48, 48];
const FIT_MAX_ZOOM = 12;
const SINGLE_MARKER_ZOOM = 12;
const CARTO_TILE_URL = `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${
  process.env.REACT_APP_CARTO_API_KEY ?? ""
}`;

const iconCache = {};
function getIconForColorName(colorName) {
  if (iconCache[colorName]) return iconCache[colorName];
  iconCache[colorName] = new L.Icon({
    iconUrl: `${COLOR_MARKERS_BASE}/marker-icon-${colorName}.png`,
    iconRetinaUrl: `${COLOR_MARKERS_BASE}/marker-icon-2x-${colorName}.png`,
    shadowUrl: markerShadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
  return iconCache[colorName];
}

function colorNameForUsername(username, colorByUsername) {
  return colorByUsername.get((username || "").toLowerCase()) || LEAFLET_COLOR_NAMES[0];
}

/** Recenter and zoom so visible pins sit in the middle of the frame. */
function FitVisibleMarkers({ positions }) {
  const map = useMap();
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      map.invalidateSize();
      if (!positions.length) {
        map.setView(MAP_CENTER, MAP_ZOOM);
        return;
      }
      if (positions.length === 1) {
        map.setView(positions[0], SINGLE_MARKER_ZOOM);
        return;
      }
      map.fitBounds(L.latLngBounds(positions), {
        padding: FIT_PADDING,
        maxZoom: FIT_MAX_ZOOM,
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [map, positions]);
  return null;
}

/**
 * Standalone map: CARTO tiles + colored markers filtered by usernames and table search text.
 */
const FleetLocationMap = ({
  markers = [],
  isLoadingMarkers = false,
  markersError = null,
  usernames = [],
  filterText = "",
  height = "400px",
  className = "",
}) => {
  const colorByUsername = useMemo(() => {
    const unique = [
      ...new Set(markers.map((m) => (m.username || "").toLowerCase()).filter(Boolean)),
    ].sort();
    return new Map(unique.map((u, i) => [u, LEAFLET_COLOR_NAMES[i % LEAFLET_COLOR_NAMES.length]]));
  }, [markers]);

  const visibleMarkers = useMemo(
    () => filterMarkersForFleetMap(markers, usernames, filterText),
    [markers, usernames, filterText],
  );
  const positions = useMemo(
    () => visibleMarkers.map((m) => m.position).filter(Boolean),
    [visibleMarkers],
  );

  return (
    <div
      className={className}
      style={{ height, width: "100%" }}>
      {isLoadingMarkers ? (
        <div className="flex h-full items-center justify-center rounded-xl border border-gray-100 bg-white text-sm text-gray-500">
          Loading map…
        </div>
      ) : markersError ? (
        <div className="flex h-full items-center justify-center rounded-xl border border-gray-100 bg-white px-4 text-center text-sm text-red-600">
          {markersError}
        </div>
      ) : (
        <MapContainer
          center={MAP_CENTER}
          zoom={MAP_ZOOM}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution="&copy; OpenStreetMap contributors &copy; CARTO"
            url={CARTO_TILE_URL}
          />
          <FitVisibleMarkers positions={positions} />
          {visibleMarkers.map((marker) => (
            <Marker
              key={marker.id}
              position={marker.position}
              icon={getIconForColorName(colorNameForUsername(marker.username, colorByUsername))}>
              <Popup>{marker.popupText}</Popup>
            </Marker>
          ))}
        </MapContainer>
      )}
    </div>
  );
};

export default FleetLocationMap;
